import { ServerClock } from './ServerClock';

export const DAILY_FRESHNESS_MS = 10_000;
export const DAILY_REQUEST_TIMEOUT_MS = 5_000;
export const DAILY_POLL_MS = 3_000;

type DailyTiming = { day: string; serverTime: number; resetsAt: number; revision: number };
const nordDate = new Intl.DateTimeFormat('sv-SE', {
  timeZone: 'Europe/Stockholm',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

export function validDailyTiming(value: Partial<DailyTiming>): boolean {
  const timestamp = (n: unknown): n is number =>
    typeof n === 'number' && Number.isSafeInteger(n) && n > 0 && n <= 8_640_000_000_000_000;
  return (
    typeof value.day === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(value.day) &&
    timestamp(value.serverTime) &&
    timestamp(value.resetsAt) &&
    value.resetsAt > value.serverTime &&
    nordDate.format(value.serverTime) === value.day &&
    Number.isSafeInteger(value.revision) &&
    (value.revision ?? -1) >= 0
  );
}

/** Wall time is only a suspension signal, never an authority for the challenge day. */
export function dailyClockInterrupted(
  previous: { monotonic: number; wall: number },
  next: { monotonic: number; wall: number },
): boolean {
  const elapsed = next.monotonic - previous.monotonic;
  return elapsed < 0 || elapsed > 2_500 || Math.abs(next.wall - previous.wall - elapsed) > 1_000;
}

/** Conservative server clock: charge the entire round trip against the task's lifetime. */
export class DailyChallengeFreshness {
  private readonly clock: ServerClock;
  private latest: DailyTiming | undefined;
  private freshUntil = 0;

  constructor(private readonly monotonicNow: () => number = () => performance.now()) {
    this.clock = new ServerClock(() => 0, monotonicNow);
  }

  confirm(next: DailyTiming, requestedAt: number): void {
    const now = this.monotonicNow();
    const elapsed = now - requestedAt;
    const previous = this.latest;
    if (previous?.day === next.day && this.resetRemaining <= 0)
      throw new Error('Daily challenge response has expired');
    if (
      !validDailyTiming(next) ||
      elapsed < 0 ||
      elapsed >= DAILY_FRESHNESS_MS ||
      (previous &&
        (next.day < previous.day ||
          next.serverTime < previous.serverTime ||
          (next.day === previous.day && next.revision < previous.revision)))
    )
      throw new Error('Invalid or outdated daily challenge response');
    const serverNow = Math.max(next.serverTime + elapsed, previous ? this.clock.now() : 0);
    if (serverNow >= next.resetsAt) throw new Error('Daily challenge response has expired');
    this.clock.synchronize(serverNow);
    const { day, revision, serverTime, resetsAt } = next;
    this.latest = { day, revision, serverTime, resetsAt };
    this.freshUntil = requestedAt + DAILY_FRESHNESS_MS;
  }

  get resetRemaining(): number {
    return this.latest ? Math.max(0, this.latest.resetsAt - this.clock.now()) : 0;
  }

  get remaining(): number {
    return Math.max(0, Math.min(this.freshUntil - this.monotonicNow(), this.resetRemaining));
  }
}
